import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { NewsletterApiService } from 'shared/services/newsletter-api.service';
import { SnackbarService } from 'shared/services/snackbar.service';
import { Subscription, take } from 'rxjs';
import { Campaign } from '../campaigns.component';

@Component({
  selector: 'app-filter-recipients-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTabsModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatPaginatorModule
  ],
  templateUrl: './filter-recipients-dialog.component.html',
  styleUrls: ['./filter-recipients-dialog.component.scss']
})
export class FilterRecipientsDialogComponent implements OnInit, OnDestroy {
  private _subscriptions: Subscription[] = [];

  filterForm: FormGroup;
  
  // Preview
  isLoadingPreview = false;
  previewCount = 0;
  totalCount = 0;

  // Existing recipients
  isLoadingRecipients = false;
  recipients: any[] = [];
  recipientsPagination = { page: 1, limit: 50, total: 0, pages: 0 };
  statusCounts: any = {};
  selectedStatus = '';

  // Assigning
  isAssigning = false;

  subscriptionStages = [
    { value: 0, label: 'FREE' },
    { value: 1, label: 'START' },
    { value: 2, label: 'PRO' },
    { value: 3, label: 'BUSINESS' }
  ];

  constructor(
    private _fb: FormBuilder,
    private _newsletterApi: NewsletterApiService,
    private _snackbar: SnackbarService,
    public dialogRef: MatDialogRef<FilterRecipientsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { campaign: Campaign }
  ) {
    this.filterForm = this._fb.group({
      subscriptionStages: [data.campaign?.filters?.subscriptionStages || []],
      hasPaidSubscription: [(data.campaign?.filters as any)?.hasPaidSubscription ?? null],
      lastSyncDaysAgo: [data.campaign?.filters?.lastSyncDaysAgo || 90]
    });
  }

  ngOnInit(): void {
    this._loadRecipients();
    this._loadPreview();
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(sub => sub.unsubscribe());
  }

  private _loadRecipients(): void {
    if (!this.data.campaign?._id) return;
    
    this.isLoadingRecipients = true;
    
    const sub = this._newsletterApi.getRecipients(
      this.data.campaign._id,
      this.recipientsPagination.page,
      this.recipientsPagination.limit,
      this.selectedStatus || undefined
    ).subscribe({
      next: (response: any) => {
        if (response.success) {
          this.recipients = response.data.recipients;
          this.recipientsPagination = response.data.pagination;
          this.statusCounts = response.data.statusCounts;
        }
        this.isLoadingRecipients = false;
      },
      error: () => {
        this.isLoadingRecipients = false;
      }
    });
    this._subscriptions.push(sub);
  }

  private _loadPreview(): void {
    this.isLoadingPreview = true;
    const formValue = this.filterForm.value;

    const filters: any = {
      lastSyncDaysAgo: formValue.lastSyncDaysAgo || 90,
      hasPaidSubscription: formValue.hasPaidSubscription,
      returnCount: true
    };
    
    // Tylko jeśli wybrano stage subskrypcji
    if (formValue.subscriptionStages && formValue.subscriptionStages.length > 0) {
      filters.subscriptionStages = formValue.subscriptionStages;
    }

    this._newsletterApi.getFilteredUsers(filters).pipe(take(1)).subscribe({
      next: (response: any) => {
        if (response.success) {
          this.previewCount = response.data.count || 0;
          this.totalCount = response.data.totalCount || 0;
        }
        this.isLoadingPreview = false;
      },
      error: () => {
        this.isLoadingPreview = false;
      }
    });
  }

  onFilterChange(): void {
    this._loadPreview();
  }

  assignRecipients(): void {
    if (!this.data.campaign?._id) return;
    
    this.isAssigning = true;
    const formValue = this.filterForm.value;

    const filters: any = {
      lastSyncDaysAgo: formValue.lastSyncDaysAgo || 90,
      hasPaidSubscription: formValue.hasPaidSubscription,
      replaceExisting: true
    };
    
    if (formValue.subscriptionStages && formValue.subscriptionStages.length > 0) {
      filters.subscriptionStages = formValue.subscriptionStages;
    }

    this._newsletterApi.assignRecipients(this.data.campaign._id, filters).pipe(take(1)).subscribe({
      next: (response: any) => {
        this.isAssigning = false;
        if (response.success) {
          this._snackbar.showSnackBar(response.message || 'Odbiorcy przypisani', 'success');
          this._loadRecipients();
        } else {
          this._snackbar.showSnackBar(response.message || 'Błąd', 'error');
        }
      },
      error: () => {
        this.isAssigning = false;
        this._snackbar.showSnackBar('Wystąpił błąd', 'error');
      }
    });
  }

  onStatusFilter(status: string): void {
    this.selectedStatus = status === this.selectedStatus ? '' : status;
    this.recipientsPagination.page = 1;
    this._loadRecipients();
  }

  onPageChange(event: PageEvent): void {
    this.recipientsPagination.page = event.pageIndex + 1;
    this.recipientsPagination.limit = event.pageSize;
    this._loadRecipients();
  }

  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      pending: 'Oczekuje',
      sent: 'Wysłano',
      opened: 'Otwarto',
      error: 'Błąd',
      unsubscribed: 'Wypisany'
    };
    return labels[status] || status;
  }

  getStatusClass(status: string): string {
    const classes: Record<string, string> = {
      pending: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
      sent: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
      opened: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
      error: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
      unsubscribed: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
    };
    return classes[status] || '';
  }

  close(result: boolean = false): void {
    this.dialogRef.close(result);
  }
}
